import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ChevronDown, ChevronLeft, ChevronRight, X } from "lucide-react";
import { useSurveyUrl, getSurveyUrl } from "./utils/surveyUrl";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// Logo assets
const LOGO_WHITE = "https://customer-assets.emergentagent.com/job_c41ac942-b289-4e5b-a0c4-9845a8b228fb/artifacts/mw1vpqkw_Asset%208%404x-8.png";
const LOGO_BLACK = "https://customer-assets.emergentagent.com/job_c41ac942-b289-4e5b-a0c4-9845a8b228fb/artifacts/hsq2iw0w_Asset%2010%404x-8.png";

// Hero image (user provided) - single static background
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_b7298b82-3b4b-4657-8b99-bf271ef0bf28/artifacts/j5jpfbff_Rhys_Sterling_editorial_photography_a_single_woman_in_sharp_f_70b364a5-5519-4bb1-8624-756cff75f3d7_2.png";

// Austin image
const AUSTIN_IMAGE = "https://images.unsplash.com/photo-1746046936858-622e86dcfa94?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxhdXN0aW4lMjB0ZXhhcyUyMGRvd250b3duJTIwYnVpbGRpbmdzJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3NDI4NTI0OXww&ixlib=rb-4.1.0&q=85";

// How it works steps (condensed to 3)
const HOW_IT_WORKS_STEPS = [
  {
    number: "01",
    title: "Tell us who you are",
    content: "Complete a detailed survey about yourself, your values, and what kind of connection you're looking for."
  },
  {
    number: "02",
    title: "Go live your life",
    content: "No swiping, no scrolling. HAEVN scans the network quietly in the background while you focus on what matters."
  },
  {
    number: "03",
    title: "Get notified when there's a match",
    content: "When someone with 80%+ compatibility joins the network, you'll both be notified at the same time. No chasing. Just alignment."
  }
];

// Persona cards for Who HAEVN Is For section (Emma first — heterosexual monogamy leads)
const PERSONA_CARDS = [
  "https://customer-assets.emergentagent.com/job_haevn-clarity-page/artifacts/icle5cfv_9.png", // Emma (Monogamous, Heterosexual)
  "https://customer-assets.emergentagent.com/job_haevn-clarity-page/artifacts/5x7mrwd6_1.png", // Taylor (31, Bisexual)
  "https://customer-assets.emergentagent.com/job_haevn-clarity-page/artifacts/yldf2o43_2.png", // David (34, Heteroflexible)
  "https://customer-assets.emergentagent.com/job_haevn-clarity-page/artifacts/wqly4o3t_3.png", // Renee & Arjun (Married, Open)
  "https://customer-assets.emergentagent.com/job_haevn-clarity-page/artifacts/ih0ywbbq_4.png", // Card 4
  "https://customer-assets.emergentagent.com/job_haevn-clarity-page/artifacts/qiyarrub_5.png", // Maya & Daniel (Married & Non-Monogamous)
  "https://customer-assets.emergentagent.com/job_haevn-clarity-page/artifacts/t6zw1jv0_6.png", // Alex & Jordan
  "https://customer-assets.emergentagent.com/job_haevn-clarity-page/artifacts/7hnx8uq2_7.png", // James (38, Monogamous, Bisexual)
  "https://customer-assets.emergentagent.com/job_haevn-clarity-page/artifacts/xptkw1ah_8.png", // Ben, Jo & Jon
];

// Who HAEVN is for cards (kept for reference but replaced by persona images)
const WHO_ITS_FOR = [
  {
    title: "Long-term seekers",
    description: "People looking for a committed, lasting relationship built on real compatibility."
  },
  {
    title: "Casual daters",
    description: "Those who want meaningful connections without the pressure of traditional dating expectations."
  },
  {
    title: "ENM & Poly",
    description: "Individuals and couples exploring ethical non-monogamy with honesty and intention."
  },
  {
    title: "Busy professionals",
    description: "People who don't have time to swipe endlessly but still want to find connection."
  },
  {
    title: "Intentional singles",
    description: "Those who know what they want and refuse to settle for less."
  },
  {
    title: "Relationship-ready",
    description: "People who have done the work and are ready to meet someone on the same page."
  }
];

// Testimonials
const TESTIMONIALS = [
  { quote: "The 80% idea makes so much sense.", author: "Jordan", location: "South Congress (SoCo)" },
  { quote: "This feels more like LinkedIn for my love life than another dating app. I like that.", author: "Maya", location: "Travis Heights" },
  { quote: "We're different. We're not confused. I like that we can just say that here.", author: "Michelle", location: "Circle C" },
  { quote: "Good luck HAEVN. Austin needs this.", author: "Malik", location: "Hyde park" },
  { quote: "When I heard there was no swiping, that was enough for me.", author: "Sofia", location: "East Austin" },
  { quote: "We're married and ENM. It's refreshing to see a system that actually understands that.", author: "Lauren", location: "Cedar Park" },
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: "What if I don't see matches right away?",
    answer: "If you don't see a match right away, that's ok. It just means there isn't an 80%+ match for you in the network yet, and HAEVN doesn't lower the standard just to show activity. You'll remain active. The system will keep scanning, and when a match is found, you'll both be notified."
  },
  {
    question: "How long does the survey take?",
    answer: "Most people finish the survey in about 8-10 minutes. The goal is to understand who you are and what kind of connection you want so HAEVN can match you with people who are truly compatible. You only need to complete it once."
  },
  {
    question: "Will my profile be public?",
    answer: "No. Your profile is not publicly searchable. You only see people who are 80% or more aligned with you, and they only see you if you're aligned with them."
  },
  {
    question: "What kinds of relationships are on HAEVN?",
    answer: "HAEVN supports many types of relationships, including: Long-term relationships, Casual dating, Friends with benefits, ENM and poly relationships. What matters isn't the structure. It's that people are honest about what they want. HAEVN matches people based on shared intention."
  },
  {
    question: "When do I pay for HAEVN?",
    answer: "You don't pay to join HAEVN or complete the survey. You only pay when you decide you're ready to access the network beyond the free preview experience. By that point, you'll already be receiving compatible matches, so you know the system is working for you. Membership gives you full access to the HAEVN network, including the ability to connect with your matches. You're not paying for matches. You're paying for access."
  }
];

// Navigation Component
const Navigation = ({ scrollPosition }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDark, setIsDark] = useState(true);
  
  useEffect(() => {
    const checkBackground = () => {
      const vh = window.innerHeight;
      
      // Check if we're in the hero (first 70% of viewport)
      if (scrollPosition < vh * 0.7) {
        setIsDark(true);
        return;
      }
      
      // Get the Austin section element and check if nav overlaps it
      const austinSection = document.querySelector('[data-testid="austin-section"]');
      if (austinSection) {
        const rect = austinSection.getBoundingClientRect();
        // If Austin section top is above nav bottom (80px) and Austin section bottom is below nav
        if (rect.top <= 60 && rect.bottom > 60) {
          setIsDark(true);
          return;
        }
      }
      
      setIsDark(false);
    };
    
    checkBackground();
  }, [scrollPosition]);
  
  const isScrolled = scrollPosition > 50;

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled && !isDark ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 
        isScrolled && isDark ? 'bg-[#1C2A3A]/95 backdrop-blur-sm' : 'bg-transparent'
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-center h-20">
          <a href="#" data-testid="nav-logo">
            <img 
              src={isDark ? LOGO_WHITE : LOGO_BLACK} 
              alt="HAEVN" 
              className="h-8 md:h-10 w-auto transition-opacity duration-300"
            />
          </a>
        </div>
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="hero-section" data-testid="hero-section">
      {/* Background image */}
      <img src={HERO_IMAGE} alt="" className="hero-bg-image opacity-100" />
      
      {/* Color overlay */}
      <div className="hero-overlay" />
      
      {/* Left-side cinematic gradient for text contrast */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-r from-[rgba(5,15,35,0.55)] via-[rgba(5,15,35,0.2)] to-transparent" />
      
      {/* Content - single cohesive left block */}
      <div className="relative z-10 flex items-center h-full w-full px-6 sm:px-12 lg:px-20 -mt-16">
        <div className="max-w-lg">
          <h1 className="heading-serif text-white text-[2.5rem] sm:text-[3rem] lg:text-[3.75rem] leading-[0.95] mb-3 animate-fade-in-up font-bold drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]" data-testid="hero-headline">
            No one needs another dating app.
          </h1>
          <h2 className="text-white/80 text-[1.5rem] sm:text-[1.625rem] lg:text-[1.875rem] leading-[1.1] mb-8 sm:mb-8 animate-fade-in-up delay-100 font-light tracking-wide drop-shadow-[0_1px_3px_rgba(0,0,0,0.25)]" style={{fontFamily: "'Roboto', Helvetica, sans-serif"}} data-testid="hero-subheadline">
            That's why we built HAEVN.
          </h2>
          {/* Desktop CTAs - inline with text */}
          <div className="hidden sm:flex flex-row gap-4 animate-fade-in-up delay-200">
            <a href={getSurveyUrl()} target="_blank" rel="noopener noreferrer" className="btn-primary-gold" data-testid="hero-primary-cta">
              Start your survey
            </a>
            <a href="#what-is-haevn" className="btn-secondary-white" data-testid="hero-secondary-cta">
              What is HAEVN
            </a>
          </div>
          <p className="hidden sm:block body-sans text-white/60 text-xs sm:text-sm mt-6 animate-fade-in-up delay-200">No commitment required. Just tell us what you're looking for.</p>
        </div>
      </div>

      {/* Mobile CTAs - pinned above the chevron */}
      <div className="sm:hidden absolute z-10 bottom-20 left-0 right-0 px-6 animate-fade-in-up delay-200">
        <div className="flex flex-col gap-3">
          <a href={getSurveyUrl()} target="_blank" rel="noopener noreferrer" className="btn-primary-gold w-full text-center" data-testid="hero-primary-cta-mobile">
            Start your survey
          </a>
          <a href="#what-is-haevn" className="btn-secondary-white w-full text-center" data-testid="hero-secondary-cta-mobile">
            What is HAEVN
          </a>
          <p className="body-sans text-white/60 text-xs mt-2">No commitment required. Just tell us what you're looking for.</p>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown className="text-white/60" size={32} />
      </div>
    </section>
  );
};

// Trust bar avatar images
const TRUST_AVATARS = [
  "https://customer-assets.emergentagent.com/job_b7298b82-3b4b-4657-8b99-bf271ef0bf28/artifacts/71cpvtoq_Rhys_Sterling_Candid_photo_of_a_happy_lesbian_couple_--ar_34__4ab7917b-b881-438e-8341-92c1f1597aba_0.png",
  "https://customer-assets.emergentagent.com/job_b7298b82-3b4b-4657-8b99-bf271ef0bf28/artifacts/urt64t7k_Rhys_Sterling_candid_photo_of_a_slightly_amused_woman_full_co_68bc7442-8721-4f56-8764-80560d0994fb_0.png",
  "https://customer-assets.emergentagent.com/job_b7298b82-3b4b-4657-8b99-bf271ef0bf28/artifacts/lc654hzv_Rhys_Sterling_httpss.mj.runKcMpilRjZiY_Draw_this_man_but_in_s_643e2fba-5059-4fd0-bfb5-dcc1b4c3ab1f_2.png",
  "https://customer-assets.emergentagent.com/job_b7298b82-3b4b-4657-8b99-bf271ef0bf28/artifacts/noryrv7w_Rhys_Sterling_httpss.mj.run70wNixDUbBQ_Man_casually_sitting_i_c6877ffb-c17b-4c09-8d87-0e415b3c93b7_3.png",
];

// Trust Bar - social proof below hero
const TrustBar = () => {
  return (
    <div className="bg-[#1C2A3A] py-4 sm:py-5" data-testid="trust-bar">
      <div className="max-w-4xl mx-auto px-5 sm:px-6 flex items-center justify-center gap-3">
        <div className="flex -space-x-2">
          {TRUST_AVATARS.map((img, i) => (
            <img key={i} src={img} alt="" className="w-8 h-8 rounded-full border-2 border-[#1C2A3A] object-cover object-top" />
          ))}
        </div>
        <p className="body-sans text-white/80 text-sm sm:text-base">
          People in Austin are already joining the network. <span className="text-[#E29E0C] font-medium">Join them.</span>
        </p>
      </div>
    </div>
  );
};

// Sticky Mobile CTA
const StickyMobileCTA = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > window.innerHeight * 0.6);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div 
      className={`fixed bottom-0 left-0 right-0 z-50 sm:hidden transition-transform duration-300 ${visible ? 'translate-y-0' : 'translate-y-full'}`}
      data-testid="sticky-mobile-cta"
    >
      <div className="bg-white border-t border-[#E5E7EB] px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]">
        <a href={getSurveyUrl()} target="_blank" rel="noopener noreferrer" className="btn-primary-gold w-full block text-center" data-testid="sticky-cta-button">
          Start your survey
        </a>
      </div>
    </div>
  );
};

// Problem Section
const ProblemSection = () => {
  return (
    <section className="py-20 sm:py-24 md:py-32 lg:py-40 bg-white" data-testid="problem-section">
      <div className="max-w-3xl mx-auto px-5 sm:px-6 text-center">
        <span className="overline text-[#4B5563] mb-6 sm:mb-8 block" data-testid="problem-overline">THE PROBLEM</span>
        
        <h2 className="heading-serif text-[#1A1A1A] text-[1.625rem] sm:text-3xl md:text-4xl lg:text-5xl leading-[1.2] mb-10 sm:mb-12" data-testid="problem-headline">
          Finding the right person shouldn't feel like a full-time job.
        </h2>
        
        <div className="space-y-8 sm:space-y-10 max-w-2xl mx-auto">
          <p className="body-sans text-[#4B5563] text-base sm:text-lg md:text-xl leading-relaxed" data-testid="problem-description">
            Dating apps turned connection into constant effort.<br />
            Swiping, filtering, guessing, repeating.
          </p>
          
          <p className="heading-serif text-[#1A1A1A] text-lg sm:text-xl md:text-2xl leading-snug" data-testid="problem-statement-1">
            You're not meeting better people.<br />
            You're just seeing more of the wrong ones.
          </p>
          
          <p className="body-sans text-[#4B5563] text-base sm:text-lg md:text-xl leading-relaxed" data-testid="problem-statement-2">
            Connection shouldn't be something you chase.
          </p>

          <p className="body-sans text-[#4B5563] text-base sm:text-lg md:text-xl leading-relaxed" data-testid="problem-statement-3">
            You should define what you want<br />
            and be notified when it actually exists.
          </p>
        </div>

        <div className="mt-12 sm:mt-14">
          <a href={getSurveyUrl()} target="_blank" rel="noopener noreferrer" className="btn-primary-gold w-full sm:w-auto" data-testid="problem-cta">
            Start your survey
          </a>
          <p className="body-sans text-[#4B5563] text-xs sm:text-sm mt-6">No commitment required. Just tell us what you're looking for.</p>
        </div>
      </div>
    </section>
  );
};

// What HAEVN Is Section
const WhatIsHaevnSection = () => {
  return (
    <section id="what-is-haevn" className="py-20 sm:py-28 md:py-36 lg:py-44 bg-[#F5F5F0]" data-testid="what-is-haevn-section">
      <div className="max-w-4xl mx-auto px-5 sm:px-6 text-center">
        <span className="overline text-[#4B5563] mb-8 sm:mb-10 block" data-testid="what-is-overline">WHAT IS HAEVN?</span>
        
        <h2 className="heading-serif text-[#1A1A1A] text-[1.5rem] sm:text-3xl md:text-4xl lg:text-5xl leading-[1.25] sm:leading-[1.15] mb-10 sm:mb-12 font-medium" data-testid="what-is-headline">
          HAEVN is a <span className="text-[#008080]">matching network</span> that helps people find relationships without the swiping.
        </h2>
        
        <div className="space-y-6 sm:space-y-8 max-w-2xl mx-auto">
          <p className="body-sans text-[#4B5563] text-base sm:text-lg md:text-xl leading-relaxed" data-testid="what-is-description">
            Tell us who you are and what you're looking for. Then go enjoy your life.
          </p>
          
          <p className="body-sans text-[#4B5563] text-base sm:text-lg md:text-xl leading-relaxed" data-testid="what-is-explanation">
            HAEVN searches its network quietly in the background and returns once per week, with every high-compatibility match it finds.
          </p>
        </div>
        
        <p className="heading-serif text-[#1A1A1A] text-xl sm:text-2xl md:text-3xl mt-10 sm:mt-14 mb-5 sm:mb-6 font-medium" data-testid="what-is-tagline">
          No swiping. No chaos. No games.
        </p>
        
        <p className="heading-serif text-[#008080] text-xl sm:text-2xl md:text-3xl mt-10 sm:mt-14 mb-5 sm:mb-6 font-medium" data-testid="what-is-summary">
          That's it. That's HAEVN.
        </p>
      </div>
    </section>
  );
};

// How HAEVN Works Section
const HowItWorksSection = () => {
  return (
    <section id="how-it-works" className="py-20 sm:py-28 md:py-36 lg:py-44 bg-white" data-testid="how-it-works-section">
      <div className="max-w-4xl mx-auto px-5 sm:px-6">
        <div className="text-center mb-14 sm:mb-20">
          <span className="overline text-[#4B5563] mb-6 sm:mb-8 block" data-testid="how-it-works-overline">HOW HAEVN WORKS</span>
          <h2 className="heading-serif text-[#1A1A1A] text-[1.5rem] sm:text-3xl md:text-4xl lg:text-5xl leading-[1.25] sm:leading-tight font-medium" data-testid="how-it-works-headline">
            Instead of browsing profiles, HAEVN finds compatible matches for you.
          </h2>
        </div>

        {/* Steps - always visible */}
        <div className="space-y-10 sm:space-y-16 md:space-y-20">
          {HOW_IT_WORKS_STEPS.map((step, index) => (
            <div key={index} data-testid={`step-${index + 1}`}>
              <div className="flex items-start gap-5 sm:gap-8 md:gap-12">
                <span className="step-number flex-shrink-0 text-[2.5rem] sm:text-[4rem] md:text-[5.5rem]" data-testid={`step-number-${index + 1}`}>{step.number}</span>
                <div className="flex-1 pt-1 sm:pt-3 md:pt-4">
                  <h3 className="heading-serif text-[#1A1A1A] text-xl sm:text-2xl md:text-3xl lg:text-4xl mb-3 sm:mb-5 font-medium" data-testid={`step-title-${index + 1}`}>
                    {step.title}
                  </h3>
                  <p className="body-sans text-[#4B5563] text-sm sm:text-base md:text-lg leading-relaxed max-w-xl" data-testid={`step-content-${index + 1}`}>
                    {step.content}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-14 sm:mt-20">
          <a href={getSurveyUrl()} target="_blank" rel="noopener noreferrer" className="btn-primary-gold w-full sm:w-auto" data-testid="how-it-works-cta">
            Start your survey
          </a>
          <p className="body-sans text-[#4B5563] text-xs sm:text-sm mt-6">No commitment required. Just tell us what you're looking for.</p>
        </div>
      </div>
    </section>
  );
};

// Live Demo Entry Section
const LiveDemoSection = () => {
  return (
    <section className="bg-[#1C2A3A] overflow-hidden" data-testid="live-demo-section" data-dark-section="true">
      <div className="max-w-3xl mx-auto px-5 sm:px-6 text-center pt-20 sm:pt-28 md:pt-32 pb-0">
        <span className="overline text-white/50 mb-5 sm:mb-6 block tracking-widest text-xs sm:text-sm" data-testid="demo-eyebrow">HAEVN MEMBER EXPERIENCE</span>
        <h2 className="heading-serif text-white text-[1.5rem] sm:text-3xl md:text-4xl lg:text-5xl leading-[1.1] mb-5 sm:mb-6 font-medium" data-testid="demo-headline">
          Test drive HAEVN.
        </h2>
        <p className="body-sans text-white/70 text-base sm:text-lg md:text-xl leading-relaxed mb-3">
          Explore the HAEVN match experience in a live, interactive demo.
        </p>
        <p className="body-sans text-white/70 text-base sm:text-lg md:text-xl leading-relaxed mb-8 sm:mb-10">
          Toggle settings, view real profiles, and see what a match actually looks like.
        </p>

        <a href="https://demo.haevn.co" target="_blank" rel="noopener noreferrer" className="btn-primary-gold w-full sm:w-auto inline-block text-center" data-testid="demo-cta">
          Enter the Demo
        </a>
      </div>

      {/* Phone mockup - full width container, flush bottom, clip top padding */}
      <a href="https://demo.haevn.co" target="_blank" rel="noopener noreferrer" className="group flex justify-center mt-8" data-testid="demo-phone-link">
        <img 
          src="/phone-mockup.png" 
          alt="HAEVN match notification on phone"
          className="w-full max-w-[600px] sm:max-w-[700px] h-auto object-contain transition-all duration-300 group-hover:scale-[1.02] drop-shadow-[0_8px_30px_rgba(0,0,0,0.3)] group-hover:drop-shadow-[0_12px_40px_rgba(0,0,0,0.5)]"
          data-testid="demo-phone-image"
        />
      </a>
    </section>
  );
};

// Trust & Verification Section
const TrustSection = () => {
  return (
    <section className="py-20 sm:py-28 md:py-36 lg:py-44 bg-[#F5F5F0]" data-testid="trust-section">
      <div className="max-w-3xl mx-auto px-5 sm:px-6 text-center">
        <span className="overline text-[#4B5563] mb-6 sm:mb-8 block" data-testid="trust-overline">TRUST & VERIFICATION</span>
        
        <h2 className="heading-serif text-[#1A1A1A] text-[1.5rem] sm:text-3xl md:text-4xl lg:text-5xl leading-[1.2] mb-8 sm:mb-10 font-medium" data-testid="trust-headline">
          Real People. Verified Profiles.
        </h2>
        
        <p className="body-sans text-[#4B5563] text-base sm:text-lg md:text-xl mb-5 sm:mb-6 leading-relaxed" data-testid="trust-description">
          Before members can access matches, their identity must be verified.
        </p>
        
        <p className="body-sans text-[#4B5563] text-base sm:text-lg md:text-xl mb-8 sm:mb-10 leading-relaxed" data-testid="trust-explanation">
          This helps keep the HAEVN network safe, accountable, and free from fake profiles.
        </p>
        
        <p className="heading-serif text-[#1A1A1A] text-lg sm:text-xl md:text-2xl lg:text-3xl mb-8 sm:mb-10 font-medium" data-testid="trust-tagline">
          No bots. No anonymous trolling. No fake accounts.
        </p>
        
        <p className="body-sans text-[#4B5563] text-base sm:text-lg md:text-xl mb-8 sm:mb-10 leading-relaxed" data-testid="trust-promise">
          Just real people looking for real connection.
        </p>
        
        <p className="body-sans text-[#9CA3AF] text-xs sm:text-sm md:text-base leading-relaxed max-w-xl mx-auto" data-testid="trust-disclaimer">
          Identity verification is handled securely by a trusted third-party provider. Your ID is used only to confirm identity and is never shared with other members.
        </p>
      </div>
    </section>
  );
};

// Who HAEVN Is For Section
const WhoItsForSection = () => {
  const scrollRef = useRef(null);
  const [expandedCard, setExpandedCard] = useState(null);

  const scroll = useCallback((direction) => {
    if (!scrollRef.current) return;
    const cardWidth = scrollRef.current.querySelector('[data-testid^="persona-card-"]')?.offsetWidth || 360;
    const scrollAmount = cardWidth + 24;
    scrollRef.current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
  }, []);

  return (
    <section className="py-20 sm:py-28 md:py-36 lg:py-44 bg-white" data-testid="who-its-for-section">
      <div className="max-w-6xl mx-auto px-5 sm:px-6">
        <div className="text-center mb-10 sm:mb-16">
          <span className="overline text-[#4B5563] mb-6 sm:mb-8 block" data-testid="who-its-for-overline">WHO HAEVN IS FOR</span>
          <h2 className="heading-serif text-[#1A1A1A] text-[1.5rem] sm:text-3xl md:text-4xl lg:text-5xl leading-[1.2] mb-6 sm:mb-8 font-medium" data-testid="who-its-for-headline">
            For People Who Know What They Want.
          </h2>
          <p className="body-sans text-[#4B5563] text-base sm:text-lg md:text-xl max-w-2xl mx-auto leading-relaxed" data-testid="who-its-for-description">
            Whether you're looking for a long-term partner, a casual connection, or something less traditional, HAEVN is designed for people who are clear about the kind of relationship they want.
          </p>
        </div>

        {/* Persona Cards Carousel */}
        <div className="relative group">
          {/* Desktop navigation arrows */}
          <button 
            onClick={() => scroll('left')}
            className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 rounded-full bg-white shadow-lg border border-[#E5E7EB] items-center justify-center hover:bg-[#F5F5F0] transition-all opacity-0 group-hover:opacity-100"
            data-testid="persona-scroll-left"
            aria-label="Scroll left"
          >
            <ChevronLeft className="text-[#1A1A1A]" size={22} />
          </button>
          <button 
            onClick={() => scroll('right')}
            className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 rounded-full bg-white shadow-lg border border-[#E5E7EB] items-center justify-center hover:bg-[#F5F5F0] transition-all opacity-0 group-hover:opacity-100"
            data-testid="persona-scroll-right"
            aria-label="Scroll right"
          >
            <ChevronRight className="text-[#1A1A1A]" size={22} />
          </button>

          {/* Scroll container */}
          <div 
            ref={scrollRef}
            className="flex gap-4 sm:gap-6 overflow-x-auto pb-6 snap-x snap-mandatory scrollbar-hide -mx-5 px-5 sm:-mx-6 sm:px-6 scroll-smooth"
          >
            {PERSONA_CARDS.map((image, index) => (
              <div 
                key={index}
                className="flex-shrink-0 w-[280px] sm:w-[320px] md:w-[360px] snap-center rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all cursor-pointer hover:-translate-y-1"
                data-testid={`persona-card-${index + 1}`}
                onClick={() => setExpandedCard(index)}
              >
                <img 
                  src={image} 
                  alt={`HAEVN member persona ${index + 1}`}
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
          
          {/* Scroll indicator */}
          <div className="flex justify-center gap-2 mt-4">
            <span className="body-sans text-[#9CA3AF] text-xs sm:text-sm">Tap a card to read their story</span>
          </div>
        </div>

        <div className="text-center mt-14 sm:mt-20">
          <a href={getSurveyUrl()} target="_blank" rel="noopener noreferrer" className="btn-primary-gold w-full sm:w-auto" data-testid="who-its-for-cta">
            Start your survey
          </a>
          <p className="body-sans text-[#4B5563] text-xs sm:text-sm mt-6">No commitment required. Just tell us what you're looking for.</p>
        </div>
      </div>

      {/* Expanded Card Modal */}
      {expandedCard !== null && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 sm:p-8"
          onClick={() => setExpandedCard(null)}
          data-testid="persona-modal-overlay"
        >
          <div 
            className="relative max-w-lg w-full max-h-[90vh] rounded-xl overflow-hidden shadow-2xl animate-fade-in-up"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={() => setExpandedCard(null)}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center hover:bg-black/60 transition-colors"
              data-testid="persona-modal-close"
              aria-label="Close"
            >
              <X className="text-white" size={20} />
            </button>
            <img 
              src={PERSONA_CARDS[expandedCard]} 
              alt={`HAEVN member persona ${expandedCard + 1}`}
              className="w-full h-auto object-contain"
              data-testid="persona-modal-image"
            />
          </div>

          {/* Modal navigation arrows */}
          <button 
            onClick={(e) => { e.stopPropagation(); setExpandedCard(expandedCard > 0 ? expandedCard - 1 : PERSONA_CARDS.length - 1); }}
            className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/40 transition-colors"
            data-testid="persona-modal-prev"
            aria-label="Previous card"
          >
            <ChevronLeft className="text-white" size={24} />
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); setExpandedCard(expandedCard < PERSONA_CARDS.length - 1 ? expandedCard + 1 : 0); }}
            className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/40 transition-colors"
            data-testid="persona-modal-next"
            aria-label="Next card"
          >
            <ChevronRight className="text-white" size={24} />
          </button>
        </div>
      )}
    </section>
  );
};

// Austin Section
const AustinSection = () => {
  return (
    <section 
      className="section-dark py-20 sm:py-28 md:py-36 lg:py-44 relative overflow-hidden"
      data-testid="austin-section"
    >
      {/* Background image */}
      <div 
        className="absolute inset-0 opacity-15"
        style={{
          backgroundImage: `url(${AUSTIN_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div className="absolute inset-0 bg-[#1C2A3A]/92" />
      
      <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-6 text-center">
        <span className="overline text-[#9CA3AF] mb-6 sm:mb-8 block" data-testid="austin-overline">HAEVN OPENS CITY BY CITY</span>
        
        <h2 className="heading-serif text-white text-[1.75rem] sm:text-4xl md:text-5xl lg:text-6xl leading-[1.15] mb-8 sm:mb-10 font-medium" data-testid="austin-headline">
          Austin is opening soon.
        </h2>
        
        <p className="body-sans text-white/85 text-base sm:text-lg md:text-xl mb-5 sm:mb-6 leading-relaxed max-w-2xl mx-auto" data-testid="austin-description">
          Be part of the network before matches begin.
        </p>
        
        <p className="body-sans text-white/85 text-base sm:text-lg md:text-xl mb-5 sm:mb-6 leading-relaxed max-w-2xl mx-auto" data-testid="austin-explanation">
          When 80%+ compatibility matches are found, both people are notified at the same time.
        </p>
        
        <p className="body-sans text-white/85 text-base sm:text-lg md:text-xl mb-8 sm:mb-10 leading-relaxed max-w-2xl mx-auto" data-testid="austin-match-monday">
          Matches are released each week on Match Monday as new compatible connections become available.
        </p>
        
        <p className="heading-serif text-white text-lg sm:text-xl md:text-2xl lg:text-3xl mb-10 sm:mb-14 font-medium" data-testid="austin-tagline">
          No swiping. No chasing. Just alignment.
        </p>

        <a href={getSurveyUrl()} target="_blank" rel="noopener noreferrer" className="btn-primary-gold w-full sm:w-auto" data-testid="austin-cta">
          Start your survey
        </a>
        <p className="body-sans text-white/70 text-xs sm:text-sm mt-6">No commitment required. Just tell us what you're looking for.</p>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  return (
    <section className="py-20 sm:py-28 md:py-36 lg:py-44 bg-[#1C2A3A]" data-testid="testimonials-section">
      <div className="max-w-6xl mx-auto px-5 sm:px-6">
        <div className="text-center mb-12 sm:mb-16">
          <span className="overline text-[#9CA3AF] mb-6 block">WHAT PEOPLE ARE SAYING</span>
          <h2 className="heading-serif text-white text-[1.5rem] sm:text-3xl md:text-4xl leading-[1.2] font-medium">
            Real feedback from Austin.
          </h2>
        </div>

        {/* Mobile: Horizontal swipe carousel */}
        <div className="sm:hidden">
          <div className="flex gap-4 overflow-x-auto pb-6 snap-x snap-mandatory scrollbar-hide -mx-5 px-5">
            {TESTIMONIALS.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6 flex-shrink-0 w-[85vw] snap-center"
                data-testid={`testimonial-mobile-${index + 1}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <span className="text-[#E29E0C] text-4xl font-serif leading-none">"</span>
                  <span className="text-[#E29E0C] text-4xl font-serif leading-none rotate-180">"</span>
                </div>
                <p className="heading-serif text-white text-lg leading-relaxed mb-6 text-center">
                  {testimonial.quote}
                </p>
                <div className="text-center">
                  <p className="body-sans text-white text-sm font-medium">{testimonial.author}</p>
                  <p className="body-sans text-[#9CA3AF] text-xs">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
          {/* Swipe indicator */}
          <div className="flex justify-center gap-2 mt-4">
            <span className="body-sans text-[#9CA3AF] text-xs">Swipe for more</span>
            <span className="text-[#9CA3AF]">→</span>
          </div>
        </div>

        {/* Desktop: Grid layout */}
        <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {TESTIMONIALS.map((testimonial, index) => (
            <div 
              key={index}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6 sm:p-8 hover:bg-white/10 transition-colors"
              data-testid={`testimonial-${index + 1}`}
            >
              <div className="flex justify-between items-start mb-4">
                <span className="text-[#E29E0C] text-4xl sm:text-5xl font-serif leading-none">"</span>
                <span className="text-[#E29E0C] text-4xl sm:text-5xl font-serif leading-none rotate-180">"</span>
              </div>
              <p className="heading-serif text-white text-lg sm:text-xl leading-relaxed mb-6 text-center">
                {testimonial.quote}
              </p>
              <div className="text-center">
                <p className="body-sans text-white text-sm font-medium">{testimonial.author}</p>
                <p className="body-sans text-[#9CA3AF] text-xs">{testimonial.location}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  return (
    <section className="py-20 sm:py-28 md:py-36 lg:py-44 bg-white" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-5 sm:px-6">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="heading-serif text-[#1A1A1A] text-[1.5rem] sm:text-3xl md:text-4xl lg:text-5xl leading-[1.2] font-medium" data-testid="faq-headline">
            Frequently Asked Questions
          </h2>
        </div>
        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {FAQ_ITEMS.map((item, index) => (
            <AccordionItem key={index} value={`item-${index}`} className="border-b border-[#E5E7EB] py-1 sm:py-2">
              <AccordionTrigger 
                className="heading-serif text-[#1A1A1A] text-base sm:text-lg md:text-xl lg:text-2xl text-left py-5 sm:py-6 hover:no-underline font-medium"
                data-testid={`faq-trigger-${index + 1}`}
              >
                {item.question}
              </AccordionTrigger>
              <AccordionContent 
                className="body-sans text-[#4B5563] text-sm sm:text-base md:text-lg leading-relaxed pb-6 sm:pb-8"
                data-testid={`faq-content-${index + 1}`}
              >
                {item.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTASection = () => {
  return (
    <section className="py-20 sm:py-28 md:py-36 lg:py-44 bg-[#F5F5F0]" data-testid="final-cta-section">
      <div className="max-w-3xl mx-auto px-5 sm:px-6 text-center">
        <h2 className="heading-serif text-[#1A1A1A] text-[1.5rem] sm:text-3xl md:text-4xl lg:text-5xl leading-[1.2] sm:leading-[1.15] mb-6 sm:mb-8 font-medium" data-testid="final-headline">
          Your person is out there.<br />Let us find them.
        </h2>
        
        <a href={getSurveyUrl()} target="_blank" rel="noopener noreferrer" className="btn-primary-gold-large hidden sm:inline-block sm:w-auto" data-testid="final-cta-button">
          Start your survey
        </a>
        <p className="body-sans text-[#9CA3AF] text-xs sm:text-sm mt-5 sm:mt-6 hidden sm:block">No commitment required. Just tell us what you're looking for.</p>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-[#1C2A3A] py-16" data-testid="footer">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <img src={LOGO_WHITE} alt="HAEVN" className="h-8 w-auto" data-testid="footer-logo" />
          <p className="body-sans text-[#9CA3AF] text-sm" data-testid="footer-copyright">
            © {new Date().getFullYear()} HAEVN. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Landing Page Component
const LandingPage = () => {
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen" data-testid="landing-page">
      <Navigation scrollPosition={scrollPosition} />
      <HeroSection />
      <TrustBar />
      <ProblemSection />
      <WhatIsHaevnSection />
      <HowItWorksSection />
      <LiveDemoSection />
      <TrustSection />
      <WhoItsForSection />
      <TestimonialsSection />
      <AustinSection />
      <FAQSection />
      <FinalCTASection />
      <Footer />
      <StickyMobileCTA />
    </div>
  );
};

import LandingPageB from "@/LandingPageB";
import PageA from "@/pages/PageA";
import PulsePage from "@/pages/PulsePage";
import PulseAdmin from "@/pages/PulseAdmin";
import { HelmetProvider } from "react-helmet-async";

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/a" element={<PageA />} />
          <Route path="/b" element={<LandingPageB />} />
          <Route path="/pulse" element={<PulsePage />} />
          <Route path="/admin/pulse" element={<PulseAdmin />} />
        </Routes>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
